import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Card, CardContent } from "@/components/ui/card"
import { useInitials } from "@/hooks/use-initials"
import { cn } from "@/lib/utils"
import type { WelcomeBackAggregate } from "@/types/welcome-back"

/**
 * OKS-560 — §21 AggregateCard. The premium loss-aversion moment: an
 * overlapping crew-cluster + "{brand}'s crew posted {N} times since you've
 * been away." Warm framing, never accusatory (UX Spec §2).
 *
 * Data comes from `DormancyReengagement::AggregateResolver` — count + a
 * first-name/avatar-only cluster (PII floor). Brand-internal, count-only; no
 * surname/handle/cross-brand data ever reaches this component.
 *
 * The cluster caps at {MAX_VISIBLE} faces + a "+N" overflow pill so a large
 * crew never overflows the 402px column (UX Spec §5). Avatars stagger in via
 * CSS only (`motion-safe`, honoring reduced-motion).
 *
 * # Locked testid
 *   - `welcome-back-aggregate` — wrapper
 */
interface AggregateCardProps {
  aggregate: WelcomeBackAggregate
  /** Current brand display name (headline copy). */
  brandName: string
  className?: string
}

const MAX_VISIBLE = 4

export function AggregateCard({
  aggregate,
  brandName,
  className,
}: AggregateCardProps) {
  const { t } = useTranslation()
  const getInitials = useInitials()
  const { count, cluster } = aggregate

  const visible = cluster.slice(0, MAX_VISIBLE)
  const overflow = cluster.length - visible.length

  return (
    <Card
      data-testid="welcome-back-aggregate"
      className={cn("border-accent/40 bg-accent/20", className)}
    >
      <CardContent className="flex flex-col items-center gap-3 py-6 text-center">
        {visible.length > 0 && (
          <div
            className="flex -space-x-3"
            data-testid="welcome-back-aggregate-cluster"
          >
            {visible.map((face, i) => (
              <Avatar
                key={`${face.first_name}-${i}`}
                className="border-background motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 size-10 border-2"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <AvatarImage src={face.avatar_url ?? undefined} alt="" />
                <AvatarFallback className="bg-muted text-muted-foreground text-xs font-semibold">
                  {getInitials(face.first_name)}
                </AvatarFallback>
              </Avatar>
            ))}
            {overflow > 0 && (
              <span
                className="border-background bg-muted text-muted-foreground flex size-10 items-center justify-center rounded-full border-2 text-xs font-semibold"
                data-testid="welcome-back-aggregate-overflow"
              >
                +{overflow}
              </span>
            )}
          </div>
        )}

        {/* The count is carried in TEXT (not colour alone) for SR + a11y. */}
        <p
          className="text-base font-semibold text-balance"
          data-testid="welcome-back-aggregate-copy"
        >
          {t("creator_welcome_back.aggregate_headline", {
            count,
            brand: brandName,
          })}
        </p>
      </CardContent>
    </Card>
  )
}
